/**
 * Venue canvas shapes — shared between VenueEditor and VenueMapViewer.
 *
 * Each shape is either:
 *  - A native rounded / circle / rectangle (handled with border-radius).
 *  - A preset polygon (catamaran, yacht, boat, polygon-irregular) handled with clip-path.
 *  - A user-defined custom polygon (`custom`) backed by `canvas_polygon`.
 *
 * Polygons are arrays of {x, y} expressed in percentages 0–100.
 */

export const CANVAS_SHAPE_OPTIONS = [
  { id: 'rectangle', label: 'Rect',     icon: '▭',  category: 'basic'    },
  { id: 'rounded',   label: 'Redon',    icon: '▢',  category: 'basic'    },
  { id: 'circle',    label: 'Circ',     icon: '○',  category: 'basic'    },
  { id: 'polygon',   label: 'Irreg',    icon: '⬡',  category: 'basic'    },
  { id: 'catamaran', label: 'Catamarán', icon: '⛵', category: 'nautical' },
  { id: 'yacht',     label: 'Yate',     icon: '🛥',  category: 'nautical' },
  { id: 'boat',      label: 'Lancha',   icon: '⛴',  category: 'nautical' },
  { id: 'custom',    label: 'Custom',   icon: '✎',  category: 'advanced' },
];

// Built-in polygons (percent coordinates, 0–100). Top is bow (front) for boats.
export const SHAPE_POLYGONS = {
  polygon: [
    { x: 10, y: 0 }, { x: 90, y: 0 }, { x: 100, y: 20 }, { x: 100, y: 80 },
    { x: 90, y: 100 }, { x: 10, y: 100 }, { x: 0, y: 80 }, { x: 0, y: 20 },
  ],
  catamaran: [
    { x: 5, y: 35 },   { x: 18, y: 0 },   { x: 32, y: 35 },
    { x: 50, y: 22 },
    { x: 68, y: 35 },  { x: 82, y: 0 },   { x: 95, y: 35 },
    { x: 95, y: 90 },  { x: 88, y: 100 },
    { x: 12, y: 100 }, { x: 5, y: 90 },
  ],
  yacht: [
    { x: 50, y: 0 },
    { x: 80, y: 12 },  { x: 95, y: 35 },
    { x: 95, y: 88 },  { x: 85, y: 100 },
    { x: 15, y: 100 }, { x: 5, y: 88 },
    { x: 5, y: 35 },   { x: 20, y: 12 },
  ],
  boat: [
    { x: 18, y: 0 },   { x: 82, y: 0 },
    { x: 95, y: 12 },  { x: 95, y: 92 },
    { x: 85, y: 100 }, { x: 15, y: 100 },
    { x: 5, y: 92 },   { x: 5, y: 12 },
  ],
};

/** Default polygon used when a producer first switches to "custom" mode. */
export const DEFAULT_CUSTOM_POLYGON = [
  { x: 10, y: 5 }, { x: 90, y: 5 }, { x: 95, y: 50 },
  { x: 90, y: 95 }, { x: 10, y: 95 }, { x: 5, y: 50 },
];

/**
 * Returns the CSS clip-path string for a given shape.
 * Returns 'none' if the shape should be handled by border-radius instead.
 */
export const getCanvasClipPath = (shape, customPolygon) => {
  if (!shape || shape === 'rectangle' || shape === 'rounded' || shape === 'circle') {
    return 'none';
  }
  let pts = null;
  if (shape === 'custom') {
    pts = Array.isArray(customPolygon) && customPolygon.length >= 3
      ? customPolygon
      : DEFAULT_CUSTOM_POLYGON;
  } else {
    pts = SHAPE_POLYGONS[shape];
  }
  if (!pts || pts.length < 3) return 'none';
  const coords = pts.map(p => `${Math.max(0, Math.min(100, p.x))}% ${Math.max(0, Math.min(100, p.y))}%`).join(', ');
  return `polygon(${coords})`;
};

/**
 * Border radius for shapes that don't use clip-path.
 */
export const getCanvasBorderRadius = (shape) => {
  if (shape === 'rounded') return '24px';
  if (shape === 'circle') return '50%';
  return '12px';
};
